import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, X, ChevronDown, User, LogOut, Bell, Globe } from "lucide-react";
import Marquee from "react-fast-marquee";
import Logo from "./Logo";
import { CATEGORIES } from "../lib/categories";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import SocialLinks, { FollowButton } from "./SocialLinks";

const TOP_CATS = ["national", "international", "politics", "business", "entertainment", "sports", "technology", "astrology", "viral"];

function useDateTime() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

const hindiDate = (d) => {
  try {
    return d.toLocaleDateString("hi-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d.toDateString();
  }
};

export default function Header({ lang, setLang }) {
  const navigate = useNavigate();
  const now = useDateTime();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [breaking, setBreaking] = useState([]);

  useEffect(() => {
    api.get("/articles", { params: { breaking: true, limit: 8 } })
      .then((r) => setBreaking(r.data || []))
      .catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setSearchOpen(false);
    setQ("");
  };

  const time = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Top utility bar */}
      <div className="hidden md:block bg-zinc-900 text-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-1.5 flex items-center justify-between text-[12px] font-hind">
          <div className="flex items-center gap-3 text-zinc-400">
            <span className="font-devanagari">{hindiDate(now)}</span>
            <span className="h-1 w-1 rounded-full bg-zinc-600" />
            <span>{time} IST</span>
          </div>
          <div className="flex items-center gap-4">
            <SocialLinks variant="light" size="sm" />
            <button
              onClick={() => setLang(lang === "hi" ? "en" : "hi")}
              data-testid="lang-toggle"
              className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 hover:bg-white/10 transition"
            >
              <Globe className="h-3 w-3" />
              {lang === "hi" ? "हिं/EN" : "EN/हिं"}
            </button>
          </div>
        </div>
      </div>

      {/* Breaking news ticker */}
      <div className="bg-gradient-to-r from-[#E30613] to-[#F4B400] text-white">
        <div className="max-w-7xl mx-auto px-4 flex items-center h-9">
          <div className="flex items-center gap-2 pr-3 mr-3 border-r border-white/30 shrink-0">
            <span className="inline-flex h-2 w-2 rounded-full bg-white bodh-pulse" />
            <span className="font-outfit font-bold text-xs uppercase tracking-widest">Breaking</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <Marquee gradient={false} speed={45} pauseOnHover>
              {(breaking.length ? breaking : [{ id: "x", title: "Bodh Networks पर हिंदी की सबसे तेज खबरें — Live Updates जल्द ही" }]).map((b, i) => (
                <Link key={b.id || i} to={b.slug ? `/article/${b.slug}` : "/"} className="font-devanagari text-sm font-medium mr-12 hover:underline" data-testid={`ticker-item-${i}`}>
                  • {b.title}
                </Link>
              ))}
            </Marquee>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="backdrop-blur-xl bg-white/85 border-b border-zinc-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="h-28 flex items-center justify-between gap-4">
            <Logo size="md" />
            <nav className="hidden lg:flex items-center gap-1">
              {TOP_CATS.map((slug) => {
                const c = CATEGORIES.find((x) => x.slug === slug);
                return (
                  <Link
                    key={slug}
                    to={`/category/${slug}`}
                    data-testid={`nav-cat-${slug}`}
                    className="px-3 py-2 rounded-md text-[15px] font-hind font-semibold text-zinc-800 hover:text-[#E30613] hover:bg-red-50/50 transition"
                  >
                    <span className="font-devanagari">{c.name_hi}</span>
                  </Link>
                );
              })}
              <button
                onClick={() => setOpen(true)}
                data-testid="nav-more"
                className="px-3 py-2 rounded-md text-[15px] font-hind font-semibold text-zinc-700 hover:text-[#E30613] inline-flex items-center gap-1"
              >
                अधिक <ChevronDown className="h-4 w-4" />
              </button>
            </nav>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchOpen((s) => !s)}
                className="p-2 rounded-full hover:bg-zinc-100 text-zinc-700"
                data-testid="search-toggle"
                aria-label="search"
              >
                <Search className="h-5 w-5" />
              </button>
              <button className="hidden md:inline-flex p-2 rounded-full hover:bg-zinc-100 text-zinc-700" data-testid="notifications-btn" aria-label="notifications">
                <Bell className="h-5 w-5" />
              </button>
              {user && (
                <div className="hidden md:flex items-center gap-2">
                  {(user.role === "admin" || user.role === "editor" || user.role === "reporter") && (
                    <Link to="/admin" data-testid="admin-link" className="text-xs font-outfit font-bold px-3 py-1.5 rounded-full bodh-gradient text-white shadow-sm hover:opacity-90">
                      Newsroom
                    </Link>
                  )}
                  <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-full hover:bg-zinc-100" title="Logout">
                    <LogOut className="h-5 w-5 text-zinc-700" />
                  </button>
                </div>
              )}
              <button
                onClick={() => setOpen((o) => !o)}
                data-testid="mobile-menu-toggle"
                className="lg:hidden p-2 rounded-md hover:bg-zinc-100"
              >
                {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <form onSubmit={onSearch} className="pb-3" data-testid="search-form">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="खबरें खोजें..."
                  data-testid="search-input"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-zinc-50 border border-zinc-200 focus:border-[#E30613] outline-none font-devanagari"
                />
              </div>
            </form>
          )}
        </div>

        {/* Mobile / mega menu */}
        {open && (
          <div className="border-t border-zinc-100 bg-white" data-testid="mobile-menu">
            <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-cat-${c.slug}`}
                  className="px-3 py-2 rounded-lg font-devanagari text-sm font-semibold hover:bg-red-50 hover:text-[#E30613]"
                >
                  {c.name_hi}
                </Link>
              ))}
              {user?.role && ["admin","editor","reporter"].includes(user.role) && (
                <Link to="/admin" onClick={() => setOpen(false)} data-testid="mobile-admin" className="col-span-2 px-3 py-2 rounded-lg bodh-gradient text-white text-center font-outfit font-bold">
                  Newsroom Dashboard
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
